import { useState } from 'react';
import { useNavigate } from 'react-router';
import type { AdminTwin } from '../../types';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeader,
  TableCell,
} from '../ui/Table';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { Modal } from '../ui/Modal';
import { formatDate } from '../../utils/helpers';
import { api } from '../../services/api';
import { useToast } from '../ui/Toast';
import { ChevronUp, ChevronDown, ChevronsUpDown, ExternalLink, Trash2 } from 'lucide-react';

interface TwinsTableProps {
  twins: AdminTwin[];
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  onSort?: (field: string) => void;
  onTwinsChanged?: () => void | Promise<void>;
}

function SortIcon({ field, sortBy, sortDir }: { field: string; sortBy?: string; sortDir?: 'asc' | 'desc' }) {
  if (sortBy !== field) return <ChevronsUpDown className="w-3 h-3 opacity-40 shrink-0" />;
  return sortDir === 'asc'
    ? <ChevronUp className="w-3 h-3 text-[#3b82f6] shrink-0" />
    : <ChevronDown className="w-3 h-3 text-[#3b82f6] shrink-0" />;
}

export function TwinsTable({ twins, sortBy, sortDir, onSort, onTwinsChanged }: TwinsTableProps) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [twinPendingDelete, setTwinPendingDelete] = useState<AdminTwin | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleConfirmDelete = async () => {
    if (!twinPendingDelete) return;
    const ownerId = twinPendingDelete.owner.id;
    if (!ownerId) {
      toast('Cannot delete: twin has no owner id.', 'error');
      return;
    }
    setDeleting(true);
    try {
      const res = await api.deleteUserTwin(ownerId, twinPendingDelete.id);
      const extra =
        res.deletedUserChats > 0 || res.deletedTwinChats > 0
          ? ` (removed ${res.deletedUserChats} match chats, ${res.deletedTwinChats} twin transcripts)`
          : '';
      toast((res.message || 'Twin deleted') + extra, 'success');
      setTwinPendingDelete(null);
      await onTwinsChanged?.();
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to delete twin', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const getTypeBadge = (type: string) => {
    switch (type) {
      case 'company':    return <Badge variant="info">Company</Badge>;
      case 'job_seeker': return <Badge variant="success">Job Seeker</Badge>;
      default:           return <Badge>Unknown</Badge>;
    }
  };

  const getStatusBadge = (twin: AdminTwin) => {
    if (twin.locked)  return <Badge variant="warning">Locked</Badge>;
    if (twin.active)  return <Badge variant="success">Active</Badge>;
    return <Badge variant="error">Inactive</Badge>;
  };

  const SH = ({ field, label }: { field: string; label: string }) => (
    <TableHeader sortable onClick={() => onSort?.(field)}>
      <span className={`inline-flex items-center gap-1 ${sortBy === field ? 'text-[#f1f5f9]' : ''}`}>
        {label}
        <SortIcon field={field} sortBy={sortBy} sortDir={sortDir} />
      </span>
    </TableHeader>
  );

  return (
    <>
      <div className="overflow-x-auto">
        <Table>
          <TableHead>
            <TableRow>
              <SH field="name"          label="Twin" />
              <TableHeader>Owner</TableHeader>
              <SH field="type"          label="Type" />
              <SH field="understanding" label="Understanding" />
              <SH field="matchCount"    label="Matches" />
              <TableHeader>Status</TableHeader>
              <SH field="createdAt"     label="Created" />
              <TableHeader className="w-px whitespace-nowrap text-right">Actions</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {twins.map((twin) => (
              <TableRow key={twin.id} hover>
                <TableCell>
                  <button
                    onClick={() => navigate(`/twins/${twin.id}`)}
                    className="flex items-center gap-3 group text-left"
                  >
                    <div className="w-9 h-9 rounded-xl gradient-blue flex items-center justify-center text-xs font-semibold text-white shrink-0">
                      {twin.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-medium text-[#f1f5f9] text-sm group-hover:text-[#3b82f6] transition-colors">{twin.name}</div>
                      <div className="text-xs text-[#64748b]">{twin.id.slice(0, 8)}…</div>
                    </div>
                  </button>
                </TableCell>
                <TableCell>
                  <button
                    onClick={() => navigate(`/users/${twin.owner.id}`)}
                    className="group flex items-center gap-1 text-left hover:text-[#3b82f6] transition-colors"
                  >
                    <div>
                      <div className="font-medium text-[#f1f5f9] text-sm group-hover:text-[#3b82f6] transition-colors">
                        {twin.owner.name}
                      </div>
                      <div className="text-xs text-[#64748b]">{twin.owner.email}</div>
                    </div>
                    <ExternalLink className="w-3 h-3 text-[#64748b] group-hover:text-[#3b82f6] opacity-0 group-hover:opacity-100 transition-all shrink-0" />
                  </button>
                </TableCell>
                <TableCell>{getTypeBadge(twin.type)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 max-w-[80px] h-1.5 bg-[#252535] rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${twin.understanding}%`,
                          background: twin.understanding >= 70
                            ? '#22c55e'
                            : twin.understanding >= 40
                            ? '#3b82f6'
                            : '#f59e0b',
                        }}
                      />
                    </div>
                    <span className="text-sm font-medium text-[#94a3b8] tabular-nums w-8 text-right">
                      {twin.understanding}%
                    </span>
                  </div>
                </TableCell>
                <TableCell>
                  <span className="text-sm font-medium text-[#94a3b8] tabular-nums">
                    {twin.matchCount}
                  </span>
                </TableCell>
                <TableCell>{getStatusBadge(twin)}</TableCell>
                <TableCell className="text-[#94a3b8]">{formatDate(twin.createdAt)}</TableCell>
                <TableCell className="text-right">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setTwinPendingDelete(twin);
                    }}
                    className="inline-flex items-center justify-center rounded-lg p-2 text-[#f87171] hover:bg-[#3f1d1d]/60 hover:text-[#fca5a5] border border-transparent hover:border-[#7f1d1d]/80 transition-colors"
                    title="Delete twin and all related data"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Modal
        isOpen={Boolean(twinPendingDelete)}
        onClose={() => !deleting && setTwinPendingDelete(null)}
        title="Delete twin"
        size="sm"
        closeOnBackdropClick={!deleting}
        allowDismiss={!deleting}
        footer={
          <>
            <Button variant="ghost" onClick={() => setTwinPendingDelete(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="danger" onClick={() => void handleConfirmDelete()} loading={deleting}>
              Delete twin
            </Button>
          </>
        }
      >
        {twinPendingDelete ? (
          <div className="space-y-3">
            <p className="text-[#94a3b8]">
              This permanently removes{' '}
              <span className="text-[#f8fafc] font-medium">{twinPendingDelete.name}</span> for{' '}
              <span className="text-[#f8fafc] font-medium">{twinPendingDelete.owner.name}</span> (
              {twinPendingDelete.owner.email}) including match chats, twin transcripts, cache entries,
              and both Firestore twin documents. This cannot be undone.
            </p>
            <p className="text-[#64748b] text-sm">
              Twin ID: <span className="font-mono text-[#94a3b8]">{twinPendingDelete.id}</span>
            </p>
          </div>
        ) : null}
      </Modal>
    </>
  );
}
